<template>
    <div>
        <LoadingSpinner v-if="loading && !svgRendered" />

        <div v-else-if="!loading && svgRendered" class="tw:space-y-4">
            <div class="tw:bg-white tw:rounded-xl tw:shadow-sm tw:border tw:border-gray-200 tw:p-4">
                <LeafletComponent
                    :svgRendered="svgRendered"
                    :originalColor="originalColor"
                    @isPanning="(e) => isPanning = e"
                />

                <div class="tw:mt-3">
                    <SeatLegend :items="legendItems" />
                </div>

                <div class="tw:flex tw:gap-3 tw:mt-4">
                    <Button @click.prevent="selectAllButacas">
                        {{ plazas ? 'Seleccionar/Limpiar todas' : 'Seleccionar todas' }}
                    </Button>
                    <Button
                        v-if="plazasSeleccionadas && plazasSeleccionadas.length > 0"
                        @click.prevent="clearAllButacas"
                    >
                        Limpiar todas
                    </Button>
                </div>

                <div class="tw:grid tw:grid-cols-3 tw:gap-4 tw:mt-4">
                    <div class="tw:text-sm">
                        <span class="tw:font-semibold">AFORO REGIÓN:</span>
                        <span class="tw:ml-1">{{ aforoTotal }}</span>
                    </div>
                    <div class="tw:text-sm">
                        <span class="tw:font-semibold">AFORO BLOQUEADO:</span>
                        <span class="tw:ml-1">{{ plazasBloqueadas }}</span>
                    </div>
                    <div class="tw:text-sm">
                        <span class="tw:font-semibold">AFORO A LA VENTA:</span>
                        <span class="tw:ml-1">{{ plazasLibres }}</span>
                    </div>
                </div>
            </div>

            <div class="tw:bg-white tw:rounded-xl tw:shadow-sm tw:border tw:border-gray-200 tw:p-4 tw:space-y-4">
                <BaseInputText :value="concepto" @input="val => concepto = val" label="Concepto" />

                <div>
                    <EtiquetaSelect @seleccionada="onEtiquetaSeleccionada" />
                </div>

                <div>
                    <Button
                        class="tw:w-full"
                        type="button"
                        @click="handleBloquear"
                        :disabled="!(plazasSeleccionadas.length > 0) || bloqueando"
                    >
                        Bloquear / Liberar
                    </Button>
                    <LoadingSpinner v-if="bloqueando" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, computed, onMounted } from 'vue'
    import store from '@/store'
    import LeafletComponent from '@/components/svg/components/LeafletComponent.vue'
    import SeatLegend from '@/modules/admin/ui/SeatLegend.vue'
    import EtiquetaSelect from '@/modules/admin/etiquetas/components/EtiquetaSelect.vue'
    import Button from '@/modules/admin/ui/Button.vue'
    import LoadingSpinner from '@/modules/admin/ui/LoadingSpinner.vue'
    import BaseInputText from '@/modules/admin/ui/BaseInputText.vue'
    import { useSvgRenderer } from '@/composables/admin/abonos/useSvgRenderer'
    import { useSeatSelection } from '@/composables/admin/abonos/useSeatSelection'
    import { useBlockSeats } from '@/composables/admin/abonos/useBlockSeats'


    const isPanning = ref(false)
    const plazas = ref(null)

    const {
        svgRendered,
        loading,
        originalColor,
        renderLeafletSvg,
        getSvg,
        getButacas,
        colors: svgColors
    } = useSvgRenderer()

    const {
        plazasSeleccionadas,
        asientos,
        clickButaca,
        selectAllButacas,
        clearAllButacas
    } = useSeatSelection(plazas)

    const {
        bloqueando,
        concepto,
        etiqueta,
        bloquearAsientos
    } = useBlockSeats()

    const recinto = computed(() => store.getters.getRecinto)
    const isTipoSecciones = computed(() => recinto.value?.tipo_proceso_online === 2)

    const plazasBloqueadas = computed(() => {
        if (!plazas.value) return 0
        return plazas.value.filter(plaza => plaza.ocupada && plaza.ocupada.bloqueada).length
    })

    const plazasLibres = computed(() => {
        if (!plazas.value) return 0
        return plazas.value.filter(plaza => plaza.libre).length
    })

    const aforoTotal = computed(() => {
        if (!plazas.value) return 0
        return plazas.value.length
    })

    const legendItems = [
        { color: svgColors.libre, label: 'Libre' },
        { color: 'gray', label: 'No disponible' },
        { color: svgColors.seleccionada, label: 'Seleccionada' },
        { color: svgColors.bloqueada, label: 'Bloqueada' },
        { color: svgColors.abonadoNoRenovado, label: 'Abonado no renovado' },
        { color: svgColors.vendidaAbonos, label: 'Vendida (abonos)' },
        { color: svgColors.vendidaEventos, label: 'Vendida (eventos)' },
        { color: svgColors.bloqueadaEventos, label: 'Bloqueada (eventos)' },
        { color: svgColors.enSesion, label: 'En sesión (abonos)' },
        { color: svgColors.enSesionEventos, label: 'En sesión (eventos)' }
    ]

    function onEtiquetaSeleccionada(etiquetaSeleccionada) {
        etiqueta.value = etiquetaSeleccionada
    }

    function handleBloquear() {
        bloquearAsientos(asientos, () => {
            svgRendered.value = null
            plazasSeleccionadas.value = []
            loadButacas()
        })
    }

    async function loadButacas() {
        try {
            const data = await getButacas(
                store.getters.getListaId,
                recinto.value.id
            )
            plazas.value = data

            const svgString = await getSvg()
            if (svgString) {
                renderLeafletSvg(svgString, plazas.value, {
                    onPlazaClick: plaza => clickButaca(plaza),
                    isPanning
                })
            }
        } catch (e) {
            console.error('Error loading butacas:', e)
        }
    }

    onMounted(() => {
        if (!isTipoSecciones.value) {
            loadButacas()
        }
    })
</script>
